import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import dayjs from "dayjs";
import { alpha } from "@mui/material/styles";
import {
  getDocumentosLogistica,
  getDocumentosLogisticaPorEnvio,
  subirDocumentoLogistica,
  eliminarDocumentoLogistica,
} from "../../api/Logistica/documentosLogisticaApi";
import LogisticaDocThumbnail from "./LogisticaDocThumbnail";
import {
  LOGISTICA_DOC_TYPES,
  logisticaDocDisplayName,
  logisticaDocPublicUrl,
} from "./logisticaUtils";

const tipos = LOGISTICA_DOC_TYPES.map((item) => item.tipo);

/**
 * @param {number} [productId] — documentos por producto (legado)
 * @param {number} [logisticaEnvioId] — documentos por envío (preferido si viene informado)
 */
const DocumentosLogistica = ({
  productId,
  logisticaEnvioId,
  /** En CP a veces coincide con ProductId de la fila (p. ej. IdPresupuesto). */
  idPresupuesto = null,
  /** En drawer CP / paneles estrechos: ancho 100 % y título en español. */
  embedded = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [documentos, setDocumentos] = useState([]);
  const [archivo, setArchivo] = useState(null);
  const [tipo, setTipo] = useState("");
  const [fecha, setFecha] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  const modoEnvio = Boolean(logisticaEnvioId);

  useEffect(() => {
    if (modoEnvio && logisticaEnvioId) {
      loadDocs();
      return;
    }
    if (!modoEnvio && productId) {
      loadDocs();
    }
  }, [productId, idPresupuesto, logisticaEnvioId, modoEnvio]);

  const loadDocs = async () => {
    if (modoEnvio) {
      const data = await getDocumentosLogisticaPorEnvio(logisticaEnvioId);
      setDocumentos(data);
      return;
    }
    let data = await getDocumentosLogistica(productId);
    const idPres = idPresupuesto != null ? Number(idPresupuesto) : null;
    const pid = productId != null ? Number(productId) : null;
    if (
      (!Array.isArray(data) || data.length === 0) &&
      idPres != null &&
      Number.isFinite(idPres) &&
      idPres !== pid
    ) {
      data = await getDocumentosLogistica(idPres);
    }
    setDocumentos(Array.isArray(data) ? data : []);
  };

  const handleUpload = async () => {
    if (!archivo || !tipo) return;
    const formData = new FormData();
    formData.append("file", archivo);
    if (modoEnvio) {
      formData.append("logisticaEnvioId", String(logisticaEnvioId));
    } else {
      formData.append("productId", productId);
    }
    formData.append("tipoDocumento", tipo);
    formData.append("nombreDocumento", archivo.name || "");
    if (fecha) formData.append("fechaCaducidad", fecha);

    await subirDocumentoLogistica(formData);
    setArchivo(null);
    setTipo("");
    setFecha("");
    loadDocs();
  };

  const confirmDelete = (docId) => {
    setDocToDelete(docId);
    setOpenDialog(true);
  };

  const handleDelete = async () => {
    if (!docToDelete) return;
    await eliminarDocumentoLogistica(docToDelete);
    setOpenDialog(false);
    setDocToDelete(null);
    loadDocs();
  };

  if (!modoEnvio && !productId) return null;

  return (
    <Box
      sx={{
        border: isDark ? "1px solid #252828" : "1px solid #ddd",
        borderRadius: 2,

        backgroundColor: isDark ? "#252828" : "#f9f9f9",
        display: "flex",
        flexDirection: modoEnvio || embedded ? "column" : "row",
        alignItems: modoEnvio || embedded ? "stretch" : "center",
        flexWrap: modoEnvio || embedded ? "nowrap" : "wrap",
        boxShadow: embedded ? 0 : 1,
        p: embedded ? 0 : 2,
        width: modoEnvio || embedded ? "100%" : "70%",
        maxWidth: "100%",
        gap: modoEnvio || embedded ? 1.5 : 0,
        border: embedded ? "none" : undefined,
      }}
    >
      <Typography
        variant={embedded ? "subtitle2" : "h6"}
        fontWeight={800}
        gutterBottom
      >
        {modoEnvio
          ? embedded
            ? `Documentos del envío ${logisticaEnvioId}`
            : `Documents (shipment ${logisticaEnvioId})`
          : embedded
            ? "Documentos de logística"
            : "Documents"}
      </Typography>
      <Grid
        container
        spacing={1}
        alignItems="center"
        sx={{ ml: modoEnvio ? 0 : 2, width: "100%" }}
      >
        <Grid item sx={{ display: "flex", gap: 1 }}>
          <TextField
            select
            label="Tipo"
            size="small"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {tipos.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Expiration"
            size="small"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            component="label"
            title={archivo?.name || "Select file"}
            sx={{
              width: "15.7em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {archivo?.name ? archivo.name : "Select file"}
            <input
              hidden
              type="file"
              onChange={(e) => setArchivo(e.target.files[0])}
            />
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!archivo || !tipo}
            sx={{ width: "12.1em" }}
          >
            Upload
          </Button>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          ml: embedded || modoEnvio ? 0 : 2,
        }}
      >
        {documentos.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin documentos subidos.
          </Typography>
        ) : null}
        {documentos.map((doc) => {
          const url = logisticaDocPublicUrl(doc.Archivo);
          const fileName = logisticaDocDisplayName(doc);
          const caducidad =
            doc.FechaCaducidad &&
            `Caduca: ${dayjs(doc.FechaCaducidad).format("DD/MM/YYYY")}`;

          return (
            <Box
              key={doc.id_Documento}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                py: 0.75,
                px: 1,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                "&:hover": {
                  backgroundColor: alpha(
                    theme.palette.primary.main,
                    isDark ? 0.1 : 0.05,
                  ),
                },
              }}
            >
              <LogisticaDocThumbnail
                url={url}
                fileName={fileName}
                label={doc.TipoDocumento}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontSize="0.85rem" fontWeight={700} noWrap>
                  {doc.TipoDocumento}
                </Typography>
                <Typography
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    textDecoration: "underline",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fileName}
                </Typography>
                {caducidad ? (
                  <Typography variant="caption" color="text.secondary">
                    {caducidad}
                  </Typography>
                ) : null}
              </Box>
              <IconButton
                size="small"
                onClick={() => confirmDelete(doc.id_Documento)}
                aria-label="Eliminar documento"
              >
                <Delete fontSize="small" sx={{ color: "error.main" }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone. Are you sure you want to delete this
            document?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentosLogistica;
